import React, { useCallback, useEffect } from "react";
import { Button, Subtitle2, mergeClasses, tokens } from "@fluentui/react-components";
import { useStyles } from "./OrderStatusStyles";
import { Receipt24Filled } from "@fluentui/react-icons";
import { HelperUtils, appInsightsEvents, trackEvent, useAppStore } from "../../../common";
import { Order, OrderStatus, PatchOrderRequest } from "@dining/api-client";
import { useNavigate } from "react-router-dom";

interface OrderStatusProblemProps {
  order?: Order;
}

export const OrderStatusProblemCard = ({ order }: OrderStatusProblemProps) => {
  const styles = useStyles();
  const helperUtils = HelperUtils.getInstance();
  const appInsights = useAppStore((state) => state.appInsights);
  const [context, client] = useAppStore((state) => [state.context, state.client]);
  const navigate = useNavigate();

  const onClick = useCallback(async () => {
    //Track user resolving order
    trackEvent(appInsights, appInsightsEvents.Usage.Actions.OrderResolved, context, {
      orderId: order?.id,
      orderNumber: order?.orderNumber,
      cafeName: order?.cafeName,
    });

    try {
      const patchOrderRequest: PatchOrderRequest = new PatchOrderRequest();
      patchOrderRequest.orderStatus = OrderStatus.CannotFulfillAcknowledged;
      await client?.patchOrder(order?.id || "", undefined, patchOrderRequest);
      navigate("/buildings");
    } catch (err) {
      appInsights.trackException(
        {
          exception: err as Error,
        },
        {
          method: "resolveOrderOnClick",
          orderId: order?.id,
          orderNumber: order?.orderNumber,
        },
      );
      throw err;
    }
  }, [context, appInsights, client, order]);

  useEffect(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "OrderStatusProblemCard",
    });
  }, [context, appInsights]);

  return (
    order && (
      <div className={styles.container} data-testid="OrderStatusProblemCard">
        <div className={mergeClasses(styles.orderNumberContainer, styles.red)}>
          <div className={styles.orderRightSection}>
            <Receipt24Filled
              className={mergeClasses(styles.icon, styles.orderIconSection)}
              primaryFill={tokens.colorNeutralBackground1}
            />
            <Subtitle2
              wrap={false}
              truncate
              className={mergeClasses(styles.orderNumberSection, styles.text, styles.white)}
            >
              Order #{helperUtils.getLastThreeDigitsOfNum(order.orderNumber)}
            </Subtitle2>
          </div>
        </div>
        <div className={styles.orderStatusContainer}>
          <div className={styles.orderRightSection}>
            <Subtitle2
              wrap={true}
              truncate
              className={mergeClasses(styles.orderProblemTextSection, styles.text)}
            >
              There was a problem getting you the status of your order
            </Subtitle2>
            <Subtitle2
              wrap={true}
              truncate
              className={mergeClasses(styles.regularText, styles.orderProblemTextSection)}
            >
              Please visit the station you ordered from to find out what happened. Sorry for the
              inconvenience.
            </Subtitle2>
          </div>
        </div>

        <div className={styles.pickupLocationContainer}>
          <Button
            appearance="primary"
            shape="rounded"
            onClick={onClick}
            className={mergeClasses(styles.orderRightSection, styles.orderResolvedBox)}
          >
            I have resolved my order
          </Button>
        </div>
      </div>
    )
  );
};
